<script>
/**
 * @component PatientQuickForm
 * @description Mini-form de creación de paciente con campos esenciales,
 *              pensado para flows inline (caso UC-REG-130: crear desde el
 *              modal de asignación de turno cuando la búsqueda no
 *              encuentra al paciente). Reusa `patientService.create`
 *              (mismo endpoint que el form completo); el backend ya
 *              maneja find-or-associate (UC-REG-118) y username
 *              cross-org (UC-REG-117).
 *
 *              Eventos:
 *              - `@created(patient)` — payload del paciente creado o
 *                asociado (con `existed: true` si reutilizó uno).
 *              - `@cancel` — el usuario quiere volver al estado previo
 *                sin crear nada.
 *
 * @example
 *   <PatientQuickForm
 *     :prefill-name="searchInput"
 *     @created="handlePatientCreated"
 *     @cancel="creating = false"
 *   />
 */
import patientService from "@/services/patient";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "PatientQuickForm",

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    /**
     * @description Texto que el médico ya tenía tipeado en el buscador;
     *              lo dejamos para que el médico no lo pierda. No mapea
     *              a un field específico — el form arranca limpio.
     */
    prefillName: { type: String, default: "" },
  },

  data() {
    return {
      isSubmitting: false,
      patient: {
        firstName: "",
        lastName: "",
        governmentId: { type: "dni", number: "" },
        bornAt: null,
        gender: "",
        email: "",
        phone: "",
      },
      docTypes: [
        { value: "dni", label: "DNI" },
        { value: "pas", label: "Pasaporte" },
        { value: "li", label: "LE/LC" },
      ],
      genders: [
        { value: "male", label: "Masculino" },
        { value: "female", label: "Femenino" },
        { value: "other", label: "Otro" },
      ],
    };
  },

  methods: {
    /**
     * @description Submit del form. Construye el payload y delega en
     *              patientService.create. Maneja `existed` y errores
     *              comunes con toasts.
     * @returns {Promise<void>}
     */
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      this.isSubmitting = true;
      try {
        const payload = {
          firstName: this.patient.firstName.trim(),
          lastName: this.patient.lastName.trim(),
          governmentId: {
            type: this.patient.governmentId.type,
            number: this.patient.governmentId.number.trim(),
          },
          ...(this.patient.bornAt && { bornAt: this.patient.bornAt }),
          ...(this.patient.gender && { gender: this.patient.gender }),
          ...(this.patient.email && { email: this.patient.email.trim() }),
          ...(this.patient.phone && { phone: this.patient.phone.trim() }),
          // username lo deriva el backend (DNI por default + sufijo si clash).
        };
        const created = await patientService.create(payload);
        this.$message({
          type: "success",
          message: "Paciente asociado a tu lista.",
          duration: 3000,
        });
        this.$emit("created", created);
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "No se pudo crear el paciente.";
        this.$message({ type: "error", message: msg, duration: 5000 });
      } finally {
        this.isSubmitting = false;
      }
    },

    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<template lang="pug">
.patient-quick-form
  .patient-quick-form__header
    button.btn-ghost.btn-sm(type="button" @click="cancel")
      i.el-icon-arrow-left
      |  Volver
    h4.patient-quick-form__title Crear paciente
  ValidationObserver(ref="observer" v-slot="{}")
    form.patient-quick-form__body(@submit.prevent="submit" novalidate)
      .form-row
        ValidationProvider(name="Nombre" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Nombre *
          el-input(v-model="patient.firstName" placeholder="Nombre" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
        ValidationProvider(name="Apellido" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Apellido *
          el-input(v-model="patient.lastName" placeholder="Apellido" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
      .form-row
        .form-field.form-field--narrow
          label.label Tipo doc
          el-select(v-model="patient.governmentId.type" size="small")
            el-option(v-for="t in docTypes" :key="t.value" :value="t.value" :label="t.label")
        ValidationProvider(name="Número" rules="required" v-slot="{ errors }" tag="div" class="form-field")
          label.label Número *
          el-input(v-model="patient.governmentId.number" placeholder="29384756" size="small")
          span.has-error(v-if="errors[0]") {{ errors[0] }}
      .form-row
        .form-field
          label.label Fecha de nacimiento
          el-date-picker(v-model="patient.bornAt" type="date" placeholder="dd/MM/yyyy" format="dd/MM/yyyy" value-format="yyyy-MM-dd" size="small" style="width:100%")
        .form-field
          label.label Género
          el-select(v-model="patient.gender" placeholder="—" size="small" clearable)
            el-option(v-for="g in genders" :key="g.value" :value="g.value" :label="g.label")
      .form-row
        .form-field
          label.label Email
          el-input(v-model="patient.email" placeholder="opcional" size="small" type="email")
        .form-field
          label.label Teléfono
          el-input(v-model="patient.phone" placeholder="opcional" size="small")
      .patient-quick-form__actions
        el-button(type="primary" size="small" :loading="isSubmitting" @click="submit") Crear y seleccionar
</template>

<style lang="scss" scoped>
.patient-quick-form {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &--narrow {
      max-width: 110px;
    }
  }

  .label {
    font-size: 12px;
    color: #5e6770;
  }

  .has-error {
    font-size: 11px;
    color: #d9534f;
  }

  &__actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
